// src/components/NavbarUploadIndicator.jsx
import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import {
  FaCheck,
  FaTimes,
  FaCloudUploadAlt,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";
import { useUploadManager } from "../context/UploadManagerContext";

const formatBytes = (bytes) => {
  if (!bytes || bytes < 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1
  );
  const value = bytes / Math.pow(1024, i);
  return `${value >= 100 || i === 0 ? Math.round(value) : value.toFixed(1)} ${
    units[i]
  }`;
};

const formatDuration = (seconds) => {
  if (!isFinite(seconds) || seconds <= 0) return "0s";
  const s = Math.round(seconds);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  if (m < 60) return `${m}m ${rem}s`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
};

const indicatorStyles = `
  .nui-wrap {
    position: relative;
    display: inline-flex;
    align-items: center;
    margin-right: 10px;
  }
  .nui-pill {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 5px 10px;
    background: #ffffff;
    border: 1px solid #d3e0f5;
    border-radius: 999px;
    box-shadow: 0 2px 8px rgba(0, 74, 173, 0.15);
    font-size: 12px;
    color: #1c2b45;
    cursor: pointer;
    transition: background 0.15s ease, transform 0.15s ease;
    user-select: none;
    max-width: 260px;
  }
  .nui-pill:hover { background: #f4f8fe; transform: translateY(-1px); }
  .nui-pill.is-done { border-color: #bfe6cb; background: #f2fbf5; }
  .nui-pill.is-error { border-color: #f0c4c4; background: #fff4f4; }
  .nui-ring { position: relative; width: 26px; height: 26px; flex: 0 0 26px; }
  .nui-ring svg { transform: rotate(-90deg); display: block; }
  .nui-ring-bg { stroke: #e3ecfb; }
  .nui-ring-fg { stroke: #004aad; stroke-linecap: round; transition: stroke-dashoffset 0.25s ease; }
  .nui-ring-fg.is-done { stroke: #1f9d55; }
  .nui-ring-fg.is-warn { stroke: #d9822b; }
  .nui-ring-txt {
    position: absolute; inset: 0;
    display: flex; align-items: center; justify-content: center;
    font-weight: 700; font-size: 8px; color: #1c2b45;
  }
  .nui-label { font-weight: 600; white-space: nowrap; }
  .nui-sub { font-size: 10px; color: #6b7a93; white-space: nowrap; }

  .nui-pop {
    position: absolute;
    top: calc(100% + 8px);
    right: 0;
    width: min(360px, calc(100vw - 20px));
    max-height: 60vh;
    display: flex;
    flex-direction: column;
    background: #fff;
    border: 1px solid #d3e0f5;
    border-radius: 12px;
    box-shadow: 0 14px 40px rgba(0, 30, 90, 0.22);
    overflow: hidden;
    z-index: 4000;
  }
  .nui-pop-head {
    display: flex; align-items: center; justify-content: space-between;
    padding: 8px 12px;
    background: linear-gradient(135deg, #004aad 0%, #0a63d8 100%);
    color: #fff;
    font-weight: 600; font-size: 12.5px;
  }
  .nui-pop-stats {
    display: flex; justify-content: space-between;
    padding: 6px 12px;
    background: #f4f8fe;
    font-size: 11px; color: #3f5272;
  }
  .nui-list { max-height: 240px; overflow-y: auto; padding: 4px 6px; }
  .nui-row {
    display: grid; grid-template-columns: 18px 1fr auto;
    gap: 6px; align-items: center;
    padding: 5px 6px; border-radius: 6px;
  }
  .nui-row:hover { background: #f4f8fe; }
  .nui-row-name {
    font-size: 11.5px; color: #1c2b45;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .nui-row-size { font-size: 10px; color: #6b7a93; }
  .nui-mini { height: 3px; margin-top: 3px; background: #e3ecfb; border-radius: 3px; overflow: hidden; }
  .nui-mini > span { display: block; height: 100%; background: #004aad; transition: width 0.2s ease; }
  .nui-mini.is-done > span { background: #1f9d55; }
  .nui-mini.is-error > span { background: #d64545; }
  .nui-status { font-size: 11px; font-weight: 600; min-width: 50px; text-align: right; color: #3f5272; }
  .nui-status.is-done { color: #1f9d55; }
  .nui-status.is-error { color: #d64545; }
  .nui-status.is-saving { color: #d9822b; }
  .nui-ico { display: inline-flex; align-items: center; justify-content: center; width: 18px; height: 18px; }
  .nui-ico.is-done { color: #1f9d55; }
  .nui-ico.is-error { color: #d64545; }
  .nui-ico.is-wait { color: #9aa8bf; }
  .nui-spin {
    width: 12px; height: 12px; border-radius: 50%;
    border: 2px solid #cfe0fa; border-top-color: #004aad;
    animation: nui-rot 0.7s linear infinite;
  }
  @keyframes nui-rot { to { transform: rotate(360deg); } }
  .nui-foot { display: flex; justify-content: flex-end; gap: 6px; padding: 6px 10px 10px; }
  .nui-btn {
    border: 1px solid #c9d7ee; background: #fff; color: #1c2b45;
    border-radius: 6px; padding: 4px 12px; font-size: 12px; font-weight: 600; cursor: pointer;
  }
  .nui-btn:hover { background: #f4f8fe; }
  .nui-btn.is-danger { color: #c93636; border-color: #f0c4c4; }
  .nui-btn.is-danger:hover { background: #fff4f4; }
  .nui-btn.is-primary { background: #004aad; border-color: #004aad; color: #fff; }
  .nui-btn.is-primary:hover { background: #003a8a; }
`;

const RADIUS = 11;
const CIRC = 2 * Math.PI * RADIUS;

const NavbarUploadIndicator = () => {
  const { items, kind, startedAt, isUploading, cancel, dismiss } =
    useUploadManager();

  const [open, setOpen] = useState(false);
  const [, setTick] = useState(0);

  // Keep elapsed/ETA fresh
  useEffect(() => {
    if (!isUploading) return undefined;
    const t = setInterval(() => setTick((n) => n + 1), 500);
    return () => clearInterval(t);
  }, [isUploading]);

  if (!items || items.length === 0) return null;

  const total = items.length;
  const doneCount = items.filter((i) => i.status === "done").length;
  const failedCount = items.filter((i) => i.status === "error").length;
  const canceledCount = items.filter((i) => i.status === "canceled").length;
  const remaining = total - doneCount - failedCount - canceledCount;
  const finished = remaining === 0;

  const totalBytes = items.reduce((s, i) => s + i.size, 0);
  const loadedBytes = items.reduce(
    (s, i) => s + (i.status === "done" ? i.size : Math.min(i.loaded, i.size)),
    0
  );
  const pct = totalBytes
    ? Math.min(100, Math.round((loadedBytes * 100) / totalBytes))
    : 0;

  const elapsed = Math.max((Date.now() - (startedAt || Date.now())) / 1000, 0.001);
  const speed = loadedBytes / elapsed;
  const eta = speed > 0 ? (totalBytes - loadedBytes) / speed : 0;

  const noun = total === 1 ? "file" : "files";
  let title = kind === "replace" ? "Replacing file" : `Uploading ${total} ${noun}`;
  if (finished) {
    if (failedCount > 0) title = "Finished with errors";
    else if (canceledCount > 0) title = "Upload canceled";
    else title = kind === "replace" ? "File replaced" : "Upload complete";
  }

  const ringOffset = CIRC * (1 - pct / 100);
  const ringClass = finished
    ? failedCount || canceledCount
      ? "is-warn"
      : "is-done"
    : "";
  const pillClass = finished
    ? failedCount || canceledCount
      ? "is-error"
      : "is-done"
    : "";

  return (
    <div className="nui-wrap">
      <style>{indicatorStyles}</style>

      <div
        className={`nui-pill ${pillClass}`}
        onClick={() => setOpen((o) => !o)}
        title={finished ? "Upload finished" : "Upload in progress"}
      >
        <div className="nui-ring">
          <svg width="26" height="26" viewBox="0 0 26 26">
            <circle
              className="nui-ring-bg"
              cx="13"
              cy="13"
              r={RADIUS}
              fill="none"
              strokeWidth="3"
            />
            <circle
              className={`nui-ring-fg ${ringClass}`}
              cx="13"
              cy="13"
              r={RADIUS}
              fill="none"
              strokeWidth="3"
              strokeDasharray={CIRC}
              strokeDashoffset={ringOffset}
            />
          </svg>
          <div className="nui-ring-txt">
            {finished && !failedCount && !canceledCount ? (
              <FaCheck size={10} />
            ) : (
              `${pct}%`
            )}
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
          <span className="nui-label">
            {finished ? "Done" : `${doneCount}/${total}`}
          </span>
          <span className="nui-sub">
            {finished
              ? `${formatBytes(totalBytes)}`
              : `${formatBytes(loadedBytes)} / ${formatBytes(totalBytes)}`}
          </span>
        </div>

        {open ? <FaChevronUp size={10} /> : <FaChevronDown size={10} />}
      </div>

      {open &&
        ReactDOM.createPortal(
          <div
            className="nui-pop"
            style={{
              position: "fixed",
              top: 64,
              right: 16,
            }}
          >
            <div className="nui-pop-head">
              <span>{title}</span>
              <span
                style={{ cursor: "pointer", fontSize: 14, lineHeight: 1 }}
                onClick={() => setOpen(false)}
              >
                ×
              </span>
            </div>

            <div className="nui-pop-stats">
              <span>
                {formatBytes(loadedBytes)} / {formatBytes(totalBytes)}
              </span>
              {finished ? (
                <span>Took {formatDuration(elapsed)}</span>
              ) : (
                <span>
                  {formatBytes(speed)}/s · {formatDuration(eta)} left
                </span>
              )}
            </div>

            <div className="nui-list">
              {items.map((item) => {
                const itemPct = item.size
                  ? Math.min(100, Math.round((item.loaded * 100) / item.size))
                  : 0;
                const saving =
                  item.status === "uploading" && item.loaded >= item.size;

                let statusText = "Waiting";
                let statusClass = "";
                if (item.status === "uploading") {
                  statusText = saving ? "Saving…" : `${itemPct}%`;
                  statusClass = saving ? "is-saving" : "";
                } else if (item.status === "done") {
                  statusText = "Done";
                  statusClass = "is-done";
                } else if (item.status === "error") {
                  statusText = "Failed";
                  statusClass = "is-error";
                } else if (item.status === "canceled") {
                  statusText = "Canceled";
                }

                return (
                  <div
                    className="nui-row"
                    key={item.id}
                    title={item.error || item.name}
                  >
                    <span
                      className={`nui-ico ${
                        item.status === "done"
                          ? "is-done"
                          : item.status === "error"
                          ? "is-error"
                          : "is-wait"
                      }`}
                    >
                      {item.status === "uploading" && (
                        <span className="nui-spin" />
                      )}
                      {item.status === "done" && <FaCheck size={10} />}
                      {item.status === "error" && <FaTimes size={10} />}
                      {(item.status === "queued" ||
                        item.status === "canceled") && (
                        <FaCloudUploadAlt size={12} />
                      )}
                    </span>

                    <div style={{ minWidth: 0 }}>
                      <div className="nui-row-name">{item.name}</div>
                      <div className="nui-row-size">
                        {item.status === "error" && item.error
                          ? item.error
                          : formatBytes(item.size)}
                      </div>
                      <div
                        className={`nui-mini ${
                          item.status === "done"
                            ? "is-done"
                            : item.status === "error"
                            ? "is-error"
                            : ""
                        }`}
                      >
                        <span
                          style={{
                            width: `${
                              item.status === "done" ? 100 : itemPct
                            }%`,
                          }}
                        />
                      </div>
                    </div>

                    <span className={`nui-status ${statusClass}`}>
                      {statusText}
                    </span>
                  </div>
                );
              })}
            </div>

            <div className="nui-foot">
              {finished ? (
                <button
                  type="button"
                  className="nui-btn is-primary"
                  onClick={() => {
                    dismiss();
                    setOpen(false);
                  }}
                >
                  Close
                </button>
              ) : (
                <button
                  type="button"
                  className="nui-btn is-danger"
                  onClick={cancel}
                >
                  Cancel upload
                </button>
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default NavbarUploadIndicator;